/**
 * The stockists map.
 *
 * A second view of the list, never a replacement for it: the addresses are in
 * the HTML and stay there. This element is hidden until the moment it works,
 * so a visitor whose network drops the CDN, or who has JavaScript off, sees
 * the list and no empty grey box where a map was promised.
 *
 * Nothing is fetched from us. Coordinates ship with the page, Leaflet is a
 * pinned file on a CDN, and the tiles come from whichever provider the section
 * settings name.
 */
class StockistMap extends HTMLElement {
  connectedCallback() {
    // Both scripts are deferred and Leaflet comes first, so in document order
    // it has run by now. `load` is the second chance for the orderings that do
    // not hold: an integrity check the browser resolves late, or a script the
    // theme moves. After that the CDN is genuinely gone and the element stays
    // hidden, which is the intended end state, not a failure to handle.
    if (typeof L === 'undefined') {
      window.addEventListener('load', () => this.build(), { once: true });
      return;
    }

    this.build();
  }

  build() {
    if (typeof L === 'undefined' || this.map) return;

    const source = this.querySelector('[ref="data"]');
    const canvas = this.querySelector('[ref="canvas"]');
    if (!source || !canvas) return;

    /** @type {Array<{i: number, name: string, address: string, url: string, lat: number, lng: number}>} */
    let shops;
    try {
      shops = JSON.parse(source.textContent);
    } catch {
      return;
    }
    if (!shops.length) return;

    this.hidden = false;

    const map = L.map(canvas, {
      scrollWheelZoom: false, // a map that eats the page scroll is a trap
      zoomControl: true,
      attributionControl: true,
    });

    L.tileLayer(this.dataset.tiles, {
      maxZoom: Number(this.dataset.maxZoom) || 17,
      attribution: this.dataset.attribution,
    }).addTo(map);

    // Read the palette rather than restating it, so the dots follow the theme
    // settings if the accent is ever changed.
    const styles = getComputedStyle(document.documentElement);
    const accent = styles.getPropertyValue('--color-accent').trim() || '#9B4257';
    const ground = styles.getPropertyValue('--color-background').trim() || '#E8E7E3';

    const markers = new Map();

    for (const shop of shops) {
      const marker = L.circleMarker([shop.lat, shop.lng], {
        radius: 8,
        color: ground,
        weight: 2,
        fillColor: accent,
        fillOpacity: 1,
      }).addTo(map);

      const link = shop.url
        ? `<a href="${escapeAttribute(shop.url)}" target="_blank" rel="noopener">Visit</a>`
        : '';
      marker.bindPopup(
        `<strong>${escapeText(shop.name)}</strong><br>${escapeText(shop.address || '')}<br>${link}`
      );

      markers.set(String(shop.i), marker);
    }

    this.map = map;

    map.fitBounds(
      L.latLngBounds(shops.map((shop) => [shop.lat, shop.lng])),
      { padding: [48, 48], maxZoom: 11 }
    );

    // The element was `hidden` a moment ago, so Leaflet measured a box the
    // layout had not settled yet. Without this the tiles are laid out against
    // the old size and the map comes up grey down one side.
    requestAnimationFrame(() => map.invalidateSize());

    if (window.ResizeObserver) {
      new ResizeObserver(() => map.invalidateSize()).observe(this);
    }

    // Clicking a row moves the map to its dot. The row is not a link, so this
    // adds a behaviour to static text rather than overriding a navigation.
    const list = this.closest('.stockists__body')?.querySelector('.stockists__list');

    list?.addEventListener('click', (event) => {
      const row = event.target.closest('[data-stockist]');
      const marker = row && markers.get(row.dataset.stockist);
      if (!marker || event.target.closest('a')) return;

      map.setView(marker.getLatLng(), 14, { animate: true });
      marker.openPopup();
    });
  }
}

/** @param {string} value */
function escapeText(value) {
  const node = document.createElement('span');
  node.textContent = value;
  return node.innerHTML;
}

/** @param {string} value */
function escapeAttribute(value) {
  return escapeText(value).replaceAll('"', '&quot;');
}

if (!customElements.get('stockist-map')) {
  customElements.define('stockist-map', StockistMap);
}
